import { Button, Text } from '@viapeople/components'
import { config } from '../config'
import { useFeatureFlag } from '../hooks/useFeatureFlag'

const DEMO_ALLOWED_ENVIRONMENTS = new Set(['local', 'dev'])

/**
 * Proof of Concept Component demonstrating feature flag usage in the client app
 * This component shows/hides content based on the "test-new-ff" feature flag
 * Organization ID is automatically taken from the logged-in user's session context
 */
export const FeatureFlagDemo = () => {
	const { isEnabled, isLoading } = useFeatureFlag('test-new-ff')

	// Hard gate: this is POC content; never render outside the environments where the
	// `test-new-ff` registry entry is offered (local/dev). Defends against a stray
	// production toggle leaking demo UI to real customers.
	if (!DEMO_ALLOWED_ENVIRONMENTS.has(config.env)) {
		return null
	}

	// Show nothing if loading or disabled
	if (isLoading || !isEnabled) {
		return null
	}

	return (
		<div className='p-6 border-2 border-primary-200 rounded-lg bg-primary-50 mb-6'>
			<div className='flex items-start justify-between mb-4'>
				<div>
					<Text variant='h4' weight='semibold' className='mb-2'>
						🎉 New Feature Enabled!
					</Text>
					<Text variant='small' className='text-neutral-700 mb-4'>
						This is a proof of concept demonstrating the "test-new-ff" feature flag. This content is only visible when
						the feature flag is enabled for your organization.
					</Text>
				</div>
			</div>

			<div className='space-y-3'>
				<div className='p-4 bg-white rounded border border-primary-200'>
					<Text variant='small' weight='semibold' className='mb-2'>
						Demo Feature Card
					</Text>
					<Text variant='xsmall' className='text-neutral-600'>
						This card represents a new feature that can be toggled on/off per organization using feature flags. This is
						displayed in the client app.
					</Text>
				</div>

				<div className='flex gap-3'>
					<Button variant='primary' size='small'>
						New Action (Demo)
					</Button>
					<Button variant='secondary' size='small'>
						Secondary Action (Demo)
					</Button>
				</div>

				<div className='mt-4 p-3 bg-white rounded border border-primary-200'>
					<Text variant='xsmall' className='text-neutral-500'>
						<strong>Feature Flag:</strong> test-new-ff | <strong>Status:</strong> Enabled ✅ | <strong>App:</strong>{' '}
						Client
					</Text>
				</div>
			</div>
		</div>
	)
}
